import { useState, useRef, useEffect, useContext } from 'react';
import { instanaReportError, instanaCustomEvent } from '@bizzabo/instana-api';

import {
    MAX_CONCURRENT_STREAMS,
    SESSION_GRACE_TIME,
    getTimeToSessionEndWithDelayMs,
    hashStringTo32bitInt,
    isMobile,
} from '../sessionBroadcastUtils';
import { viewController } from '../LiveBroadcast/constants';
import { AgendaContext } from '../AgendaContextProvider';
import FeatureToggleUtils from 'FeatureToggleUtils';
import { postAttendeeHearbeat } from '../../ApiUtil';
import { JoinLeaveNotifier } from './joinLeaveNotifier';

const HTTP_HEATBEAT_INTERVAL = 5000;

const supportAttendeeHearbeat = async(params, payload) => {
    try {
        await postAttendeeHearbeat(params, payload);
    } catch (e) {
        const instanaError = `[Attendee hearbeat]: ${e?.message}, error: ${e?.stack || e}`;
        instanaReportError({ error: instanaError });
    }
};

export const useConcurrentUsersNotifier = ({
    session,
    userEmail,
    socketConfig,
    isFeatureEnabled,
    uiController,
    virtualSessionDetails,
    ticketsSelectionData
}) => {
    const [isConcurrentUsers, setIsConcurrentUsers] = useState(false);
    const concurrentStreams = useRef(new Map());

    const isMobileDevice = isMobile();
    const { event } = useContext(AgendaContext);
    const timeZone = event?.['time-zone-id'];
    const { accountId, eventId } = socketConfig || {};
    const { endDate, endMinute, id: sessionId, startTime, endTime } = session || {};
    const { isThirdPartyCookiesSupported, streamId, bizzaboStreamId } = virtualSessionDetails || {};
    let socketStreamId = streamId;
    if (!streamId && bizzaboStreamId) {
        socketStreamId = `${bizzaboStreamId}_MAIN`;
    } else if (!streamId && sessionId) {
        socketStreamId = `${sessionId}`;
    }

    const loadSessionPageForExternalSession = FeatureToggleUtils.isFeatureEnabled('external-session-join-leave');

    const attendeeHeartbeatPollingId = useRef(null);
    const notifierRef = useRef(null);
    const isBlockedByConcurrentUse = useRef(false);
    const liveConnectionKey = useRef(null);

    const connectionKey = `${accountId}:${eventId}:${socketStreamId}:${userEmail}`;
    const hasConnectionIdentity = Boolean(userEmail && accountId && eventId);

    const stopAttendeeHeartbeatPolling = () => {
        if (attendeeHeartbeatPollingId.current) {
            clearInterval(attendeeHeartbeatPollingId.current);
            attendeeHeartbeatPollingId.current = null;
        }
    };

    const getTimeToSessionEnd = () => getTimeToSessionEndWithDelayMs(
        { endDate, endMinute },
        SESSION_GRACE_TIME,
        timeZone
    );

    const startAttendeeHeartbeatPolling = (connectionId, payload) => {
        if (!attendeeHeartbeatPollingId.current) {
            attendeeHeartbeatPollingId.current = setInterval(() => {
                if (getTimeToSessionEnd() <= 0) {
                    stopAttendeeHeartbeatPolling();
                    return;
                }
                supportAttendeeHearbeat({accountId, eventId, connectionId}, payload);
            }, HTTP_HEATBEAT_INTERVAL);
        }
    };

    const abandonNotifier = () => {
        notifierRef.current?.destroy();
        notifierRef.current = null;
        liveConnectionKey.current = null;
    };

    const teardownNotifier = () => {
        abandonNotifier();
        stopAttendeeHeartbeatPolling();
    };

    const giveUpReconnecting = (attempts) => {
        instanaCustomEvent({
            eventName: '[Avoid Concurrent User] RECONNECT LIMIT REACHED',
            meta: { email: userEmail, attempts },
        });
        abandonNotifier();
    };

    const blockForConcurrentUse = () => {
        isBlockedByConcurrentUse.current = true;
        teardownNotifier();
    };

    const onSubscribeHandler = ({ deviceId, type, timestamp }) => {
        if (type === 'INIT') {
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] INIT',
                meta: { curDeviceId: deviceId, curTS: timestamp, email: userEmail },
            });
            stopAttendeeHeartbeatPolling();
            concurrentStreams.current?.clear();
            concurrentStreams.current?.set('me', { deviceId, timestamp });
            return;
        }

        if (type === 'JOIN') {
            const me = concurrentStreams.current?.get('me');
            if (me?.timestamp <= timestamp && me?.deviceId !== deviceId) {
                instanaCustomEvent({
                    eventName: '[Avoid Concurrent User] JOIN',
                    meta: { curDeviceId: deviceId, curTS: timestamp, email: userEmail },
                });
                concurrentStreams.current?.set(deviceId, { deviceId, timestamp });
            }
        }

        if (type === 'LEAVE') {
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] LEAVE',
                meta: { curDeviceId: deviceId, curTS: timestamp, email: userEmail },
            });
            concurrentStreams.current?.delete(deviceId);
        }

        if (concurrentStreams.current?.size > MAX_CONCURRENT_STREAMS) {
            instanaCustomEvent({
                eventName: '[Avoid Concurrent User] Detected concurrent user',
                meta: { size: concurrentStreams.current?.size, curDeviceId: deviceId, curTS: timestamp, email: userEmail },
            });
            setIsConcurrentUsers(true);
            blockForConcurrentUse();
            concurrentStreams.current?.clear();
        }
    };

    const startNotifier = () => {
        teardownNotifier();

        if (!hasConnectionIdentity) {
            return;
        }

        const entityId = hashStringTo32bitInt(userEmail);
        const payload = {
            streamId: socketStreamId,
            sessionId,
            ticketId: ticketsSelectionData?.tickets?.[0]?.id,
            startTime,
            endTime
        };

        instanaCustomEvent({
            eventName: '[Avoid Concurrent User] CONNECTING',
            meta: { email: userEmail },
        });

        const notifier = new JoinLeaveNotifier({
            ...socketConfig,
            entityId,
            ...(socketStreamId && { data: payload }),
            onDegraded: () => {
                instanaCustomEvent({
                    eventName: '[Avoid Concurrent User] CONNECT PROBLEMS',
                    meta: { email: userEmail },
                });
                if (getTimeToSessionEnd() > 0) {
                    startAttendeeHeartbeatPolling(entityId, payload);
                } else {
                    stopAttendeeHeartbeatPolling();
                }
            },
            onRecovered: () => {
                instanaCustomEvent({
                    eventName: '[Avoid Concurrent User] RECONNECTED',
                    meta: { email: userEmail },
                });
            },
            onReconnectLimitReached: giveUpReconnecting,
        });

        notifierRef.current = notifier;
        liveConnectionKey.current = connectionKey;

        notifier.subscribe(onSubscribeHandler)?.catch?.((e) => {
            const instanaError = `[Avoid Concurrent User]: ${e?.message}, error: ${e?.stack || e}`;
            instanaReportError({ error: instanaError });
        });
    };

    useEffect(() => {
        const isSupportedController = uiController === viewController.SIMULIVE ||
            uiController === viewController.EMBEDDED ||
            uiController === viewController.WEBCASTING ||
            (uiController === viewController.EXTERNAL && loadSessionPageForExternalSession) ||
            (uiController === viewController.ZOOM && loadSessionPageForExternalSession) ||
            uiController === viewController.INTERACTIVE_VIDEO_PROVIDER ||
            (uiController === viewController.NEWROW_IFRAME && !isMobileDevice && isThirdPartyCookiesSupported) ||
            uiController === viewController.VIDEO_PROVIDER;

        if (!isSupportedController || !hasConnectionIdentity || isBlockedByConcurrentUse.current) {
            teardownNotifier();
            return;
        }

        if (notifierRef.current && liveConnectionKey.current === connectionKey) {
            return;
        }

        if (!isFeatureEnabled) {
            teardownNotifier();
            return;
        }

        startNotifier();
    }, [uiController, isMobileDevice, isFeatureEnabled, isThirdPartyCookiesSupported, connectionKey, loadSessionPageForExternalSession]);

    useEffect(() => () => teardownNotifier(), []);

    const connectConcurrentUsersNotifier = () => {
        setIsConcurrentUsers(false);
        isBlockedByConcurrentUse.current = false;
        startNotifier();
    };

    const disconnectConcurrentUsersNotifier = () => {
        concurrentStreams.current?.clear();
        teardownNotifier();
    };

    return {
        isConcurrentUsers,
        connectConcurrentUsersNotifier,
        disconnectConcurrentUsersNotifier,
    };
};
